import React, { useState, useRef, useEffect } from 'react';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Tooltip from '@mui/material/Tooltip';
import { PurpleSwitch } from '../../../helpers/helpers';

// Permission tiers mirror FileUploadFolder.min_permission_level on the backend.
// "employee" is the default (visible to everyone) and shows no badge.
const PERMISSION_LEVELS = [
  { value: 'employee', label: 'Everyone' },
  { value: 'manager', label: 'Managers & Admins' },
  { value: 'admin', label: 'Admins only' },
];
const PERMISSION_LABELS = {
  employee: 'Everyone',
  manager: 'Managers & Admins',
  admin: 'Admins only',
};

function FolderItem(props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [renameMode, setRenameMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState(props.folder.name);
  const [isDropTarget, setIsDropTarget] = useState(false);

  // The kebab menu must appear for anyone with an action available in it.
  // canEditDocuments covers rename/delete/AI-search; a company_admin without
  // the documents_editor override still gets the menu via onSetPermissionLevel
  // (admin-only "Who can access"). Without this, admins couldn't reach the
  // permission control even though the backend allows them to set tiers.
  const showFolderMenu = props.canEditDocuments || !!props.onSetPermissionLevel;

  const getItemCount = () => {
    if (!props.documents) return null;
    const children = Object.values(props.documents).filter(
      item => item.parentUuid === props.folder.uuid
    );
    const folderCount = children.filter(item => item.type === 'folder').length;
    const fileCount = children.filter(item => item.type === 'file').length;
    const parts = [];
    if (folderCount > 0) parts.push(`${folderCount} folder${folderCount !== 1 ? 's' : ''}`);
    if (fileCount > 0) parts.push(`${fileCount} file${fileCount !== 1 ? 's' : ''}`);
    return parts.join(', ') || 'Empty';
  };

  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when entering rename mode
  useEffect(() => {
    if (renameMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [renameMode]);

  const handleContainerClick = (e) => {
    // Only trigger folder click if we're not in rename mode and didn't click menu items
    if (!renameMode && !isMenuOpen) {
      props.handleFolderClick(props.folder.uuid);
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();
    setRenameMode(true);
    setIsMenuOpen(false);
  };

  const handleRenameConfirm = (e) => {
    e.stopPropagation();
    if (newFolderName.trim()) {
      props.rename_folder(props.folder.uuid, newFolderName);
      setRenameMode(false);
    }
  };

  const handleInputKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleRenameConfirm(e);
    } else if (e.key === 'Escape') {
      setRenameMode(false);
      setNewFolderName(props.folder.name);
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    props.delete_folder(props.folder.uuid);
    setIsMenuOpen(false);
  };

  // Drag and Drop Event Handlers
  const handleDragOver = (e) => {
    // Don't allow drops into protected folders
    if (props.folder.isProtected) {
      return;
    }
    e.preventDefault(); // Allow drop
    setIsDropTarget(true);
  };

  const handleDragLeave = (e) => {
    setIsDropTarget(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropTarget(false);

    // Don't allow drops into protected folders
    if (props.folder.isProtected) {
      return;
    }

    // Check if this is an external file drop (from desktop)
    if (e.dataTransfer.types.includes('Files') && e.dataTransfer.files.length > 0) {
      if (props.onExternalDrop) {
        props.onExternalDrop(e.dataTransfer, props.folder.uuid);
      }
      return;
    }

    // Internal move (existing logic)
    try {
      const droppedItem = JSON.parse(e.dataTransfer.getData('text/plain'));

      if (droppedItem.uuid === props.folder.uuid) {
        return;
      }

      if (droppedItem.type === 'file' && props.move_file) {
        props.move_file(droppedItem.uuid, props.folder.uuid);
      }

      if (droppedItem.type === 'folder' && props.move_folder) {
        props.move_folder(droppedItem.uuid, props.folder.uuid);
      }

    } catch (error) {
      console.error("Error moving item:", error);
    }
  };

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(props.folder));
  };

  // ownPermissionLevel = this folder's directly-set tier (drives the radio).
  // effectivePermissionLevel = strictest tier along the ancestry, resolved by
  // the backend (drives the badge — the access that actually applies).
  // isInherited = a parent imposes a stricter tier than this folder's own.
  const ownPermissionLevel = props.folder.minPermissionLevel || 'employee';
  const effectivePermissionLevel =
    props.folder.effectivePermissionLevel || ownPermissionLevel;
  const isInherited = effectivePermissionLevel !== ownPermissionLevel;

  const menuDropdown = isMenuOpen && (
    <div
      ref={menuRef}
      className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 py-1"
      style={{ top: '100%' }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          props.handleFolderClick(props.folder.uuid);
          setIsMenuOpen(false);
        }}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
      >
        Open
      </button>
      {props.canEditDocuments && !props.folder.isProtected && (
        <>
          <button
            onClick={handleRenameClick}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Rename
          </button>
          <button
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        </>
      )}
      {props.onToggleAssistantSearchable && (
        <div
          className="flex items-center justify-between px-4 py-1 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            props.onToggleAssistantSearchable(
              props.folder.uuid,
              !props.folder.assistantSearchable
            );
          }}
        >
          <span>AI Searchable</span>
          <PurpleSwitch
            size="small"
            checked={!!props.folder.assistantSearchable}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              props.onToggleAssistantSearchable(
                props.folder.uuid,
                e.target.checked
              );
            }}
          />
        </div>
      )}
      {/* Access (permission tier) — admin-only, mirrors the backend's
          admin-gated set_folder_permission_level. Hidden for protected (LMS)
          folders and the root, which the backend refuses to re-tier. Rendered
          as an inline radio-style section (not a hover flyout) to keep
          positioning simple. */}
      {props.onSetPermissionLevel && !props.folder.isProtected && props.folder.parentUuid && (
        <div className="border-t border-gray-100 mt-1 pt-1">
          <p className="px-4 py-1 text-xs font-medium text-gray-400">Who can access</p>
          {/* When an ancestor is stricter than this folder's own setting, the
              effective access is inherited. Surface it so an admin editing the
              radio understands why the folder is still locked. */}
          {isInherited && (
            <p className="px-4 pb-1 text-[11px] text-gray-400">
              Inherited from a parent folder: {PERMISSION_LABELS[effectivePermissionLevel]}
            </p>
          )}
          {PERMISSION_LEVELS.map((level) => {
            const current = props.folder.minPermissionLevel || 'employee';
            const isSelected = current === level.value;
            return (
              <div
                key={level.value}
                className="flex items-center justify-between px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSelected) {
                    props.onSetPermissionLevel(props.folder.uuid, level.value);
                  }
                  setIsMenuOpen(false);
                }}
              >
                <span>{level.label}</span>
                {isSelected && <CheckIcon style={{ fontSize: 16 }} className="text-violet-700" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Badge reflects EFFECTIVE access (strictest tier along the folder's path),
  // not just this folder's own setting — so a child left at the default inside
  // a restricted parent still shows the real, inherited restriction. Falls back
  // to the folder's own tier if the backend didn't supply an effective value.
  const permissionBadge = effectivePermissionLevel !== 'employee' ? (
    <span
      className="flex items-center gap-0.5 shrink-0 text-[11px] text-gray-500"
      title={`Who can access: ${PERMISSION_LABELS[effectivePermissionLevel]}`}
    >
      <LockOutlinedIcon style={{ fontSize: 13 }} className="text-gray-400" />
      {PERMISSION_LABELS[effectivePermissionLevel]}
    </span>
  ) : null;

  // Icon-only variant for the grid/preview cards, where the text label crowds
  // out the folder title. The "who can access" detail moves into a hover tooltip.
  const permissionBadgeIcon = effectivePermissionLevel !== 'employee' ? (
    <Tooltip title={`Who can access: ${PERMISSION_LABELS[effectivePermissionLevel]}`} arrow>
      <LockOutlinedIcon style={{ fontSize: 14 }} className="shrink-0 text-gray-400" />
    </Tooltip>
  ) : null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    // Date-ONLY strings ("2026-06-12") parse as UTC midnight, which
    // toLocaleDateString renders as the PREVIOUS day in any timezone
    // behind UTC — files created this morning showed yesterday's date.
    // Parse them as a local date instead; full ISO datetimes carry
    // their own offset and localize correctly as-is.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    const date = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (props.viewMode === 'preview') {
    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        draggable={!renameMode && props.canEditDocuments && !props.folder.isProtected}
        onDragStart={handleDragStart}
        className={`
          border rounded-lg cursor-pointer relative
          ${isDropTarget
            ? 'border-violet-700 bg-violet-50 shadow-md'
            : 'border-gray-200 hover:bg-gray-50'
          }
        `}
        onClick={handleContainerClick}
      >
        <div className="h-48 bg-gray-50 flex items-center justify-center overflow-hidden rounded-t-lg">
          <FolderIcon style={{ fontSize: 64 }} className="text-violet-200" />
        </div>
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <FolderIcon className="w-5 h-5 text-violet-700" />
              {renameMode ? (
                <div className="flex items-center space-x-2 flex-1" onClick={handleInputClick}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:border-violet-700"
                  />
                  <button
                    onClick={handleRenameConfirm}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <CheckIcon className="w-4 h-4 text-emerald-600" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {props.folder.displayName || props.folder.name}
                  </p>
                  {props.folder.isProtected && (
                    <LockIcon style={{ fontSize: 14, color: '#9CA3AF' }} className="shrink-0" />
                  )}
                  {props.folder.assistantSearchable && (
                    <AutoAwesomeIcon style={{ fontSize: 14, color: '#7c3aed' }} className="shrink-0" />
                  )}
                  {permissionBadgeIcon}
                </div>
              )}
            </div>
            {!renameMode && showFolderMenu && (
              <button
                ref={buttonRef}
                onClick={handleMenuClick}
                className="p-1 ml-2 shrink-0 hover:bg-gray-100 rounded-full"
              >
                <MoreVertIcon className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-7">{getItemCount()}</p>
        </div>
        {menuDropdown}
      </div>
    );
  }

  if (props.viewMode === 'list') {
    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        draggable={!renameMode && props.canEditDocuments && !props.folder.isProtected}
        onDragStart={handleDragStart}
        className={`
          flex items-center px-4 py-2 cursor-pointer relative border-b border-gray-100
          ${isDropTarget
            ? 'bg-violet-50'
            : 'hover:bg-gray-50'
          }
        `}
        onClick={handleContainerClick}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <FolderIcon className="w-5 h-5 text-violet-700" />
          {renameMode ? (
            <div className="flex items-center space-x-2 flex-1" onClick={handleInputClick}>
              <input
                ref={inputRef}
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={handleInputKeyDown}
                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:border-violet-700"
              />
              <button
                onClick={handleRenameConfirm}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <CheckIcon className="w-4 h-4 text-emerald-600" />
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-1 min-w-0">
              <p className="text-sm text-gray-900 truncate">
                {props.folder.displayName || props.folder.name}
              </p>
              {props.folder.isProtected && (
                <LockIcon style={{ fontSize: 14, color: '#9CA3AF' }} className="shrink-0" />
              )}
              {props.folder.assistantSearchable && (
                <AutoAwesomeIcon style={{ fontSize: 14, color: '#7c3aed' }} className="shrink-0" />
              )}
              {permissionBadge}
            </div>
          )}
        </div>
        <span className="w-36 text-right text-xs text-gray-500 shrink-0">{getItemCount()}</span>
        <span className="w-32 text-right text-xs text-gray-500 shrink-0">{formatDate(props.folder.created_at)}</span>
        <span className="w-20 text-right text-xs text-gray-400 shrink-0">Folder</span>
        {!renameMode && showFolderMenu ? (
          <button
            ref={buttonRef}
            onClick={handleMenuClick}
            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full ml-3 shrink-0"
          >
            <MoreVertIcon style={{ fontSize: 18 }} className="text-gray-400" />
          </button>
        ) : (
          <span className="w-6 h-6 ml-3 shrink-0"></span>
        )}
        {menuDropdown}
      </div>
    );
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      draggable={!renameMode && props.canEditDocuments && !props.folder.isProtected}
      onDragStart={handleDragStart}
      className={`
        p-4 border rounded-lg cursor-pointer relative flex items-center
        ${isDropTarget
          ? 'border-violet-700 bg-violet-50 shadow-md'
          : 'border-gray-200 hover:bg-gray-50'
        }
      `}
      onClick={handleContainerClick}
    >
      <div className="flex items-center space-x-3 w-full">
        <FolderIcon className="w-6 h-6 text-violet-700 shrink-0" />
        <div className="flex-1 min-w-0">
          {renameMode ? (
            <div className="flex items-center space-x-2" onClick={handleInputClick}>
              <input
                ref={inputRef}
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={handleInputKeyDown}
                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:border-violet-700"
              />
              <button
                onClick={handleRenameConfirm}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <CheckIcon className="w-5 h-5 text-emerald-600" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {props.folder.displayName || props.folder.name}
              </p>
              {props.folder.isProtected && (
                <LockIcon style={{ fontSize: 14, color: '#9CA3AF' }} className="shrink-0" />
              )}
              {props.folder.assistantSearchable && (
                <AutoAwesomeIcon style={{ fontSize: 14, color: '#7c3aed' }} className="shrink-0" />
              )}
              {permissionBadgeIcon}
            </div>
          )}
        </div>
        {!renameMode && showFolderMenu && (
          <button
            ref={buttonRef}
            onClick={handleMenuClick}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <MoreVertIcon className="w-5 h-5 text-gray-500" />
          </button>
        )}
      </div>
      {menuDropdown}
    </div>
  );
}

export default FolderItem;
